import {
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './Select.scss';
import React from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

const AppSelect: FC<AppSelectProps> = ({
  id,
  label,
  required,
  disabled,
  translated,
  placeholder,
  options = [],
  valueType = 'string',
  value,
  defaultValue,
  isRow = false,
  onChange,
  error,
}) => {
  const { t } = useTranslation();

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('message.MSE00002', { 0: t(label!) });
    }

    return error?.message ? t(error?.message) : null;
  }, [error, t, label]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string | number>) => {
      if (valueType === 'number') {
        event.target.value = Number(event.target.value);
      }
      onChange?.(event);
    },
    [onChange, valueType],
  );

  const renderValue = useCallback(
    (selected: string) => {
      if (selected) {
        const label =
          options.find((opt) => opt.value === selected)?.label ?? '';
        return translated ? label : t(label);
      }
      return <Typography>{t(placeholder!)}</Typography>;
    },
    [options, t, placeholder, translated],
  );

  return (
    <Grid
      container
      className="Select"
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      <Grid item xs={8} sx={{ width: '100%', flexDirection: 'column' }}>
        <Select
          id={id}
          name={id}
          value={value != null ? String(value) : undefined}
          defaultValue={defaultValue != null ? String(defaultValue) : undefined}
          displayEmpty
          disabled={disabled}
          IconComponent={KeyboardArrowDown}
          renderValue={renderValue}
          onChange={handleChange}
          error={!!error?.type}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                overflowY: 'auto',
              },
            },
          }}
        >
          {!required && <MenuItem value="">{t('common.all')}</MenuItem>}
          {options.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {translated ? label : t(label)}
            </MenuItem>
          ))}
        </Select>
        {errorText && <FormHelperText error>{errorText}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default AppSelect;
